
import axios from 'axios'
import partition from 'lodash/partition';
import prop from 'lodash/property';
import { dataLayerCartEvent } from '@/scripts/helpers/util';

const CART_MODAL_CLOSE_DELAY_MS = 5000;

const state = {
  checkout: {},
  sidecartOpen: false,
  kits: [],
  cartModalEnabled: window.bvaccel.cart.modalEnabled,
  cartModalOpen: false,
  cartModalTimerId: 0
}

const mutations = {
  SET_CHECKOUT (state, checkout) {
    state.checkout = checkout
  },
  OPEN_SIDECART (state) {
    state.sidecartOpen = true
  },
  OPEN_CARTMODAL (state) {
    state.cartModalOpen = true
  },
  CLOSE_CARTMODAL (state) {
    state.cartModalOpen = false
  },
  CLOSE_SIDECART (state) {
    state.sidecartOpen = false
  },
  SET_KITS(state, kits) {
    state.kits = kits
  },
  SET_CART_MODAL_TIMER_ID (state, id) {
    state.cartModalTimerId = id;
  }
}

const actions = {
  init ({ commit, dispatch }) {
    // forcing clean cart request for IE11
    let timestamp = new Date().getTime()
    let kits = []

    kits = findKits()

    axios.get(`/cart.js?q=${timestamp}`)
      .then(response => commit('SET_CHECKOUT', response.data))
      .then(() => {
        let kits = findKits()
        dispatch('setKits', kits)
      })
  },
  addItems ({ state, commit, dispatch }, items) {
    if(!(items instanceof Array))
      items = [items];
    // separate items to update from items to add
    const currentItemIds = state.checkout.items.map(prop('id'));
    const [ itemsToUpdate, itemsToAdd ] = partition(items, item => currentItemIds.includes(item.id) && !(item.properties && item.properties._kitId));
    let updatePromise, addPromise;

    const eventInfo = {
      name: 'add-to-cart',
      ecomEvent: 'Add'
    };

    const events = {
      update: [],
      add: []
    };

    if (itemsToUpdate && itemsToUpdate.length > 0) {
      const lineItemIds = state.checkout.items.map(prop('id'));
      for(const item of itemsToUpdate) {
        // construct the event with the item's quantity before adding the existing lineItem quantity.
        try {
          events.update.push(new dataLayerCartEvent(item, eventInfo));
        } catch (e) {
          console.error(e);
          dispatch('toast/send', { text: e.message, type: 'error' }, { root: true });
        }
        // get the matching line item
        const lineItem = state.checkout.items[lineItemIds.indexOf(item.id)];
        // sum quantity
        item.quantity = item.quantity + lineItem.quantity;
        // add line item key
        item.key = lineItem.key;
      }
      updatePromise = dispatch('updateItems', itemsToUpdate);
    }

    if(itemsToAdd && itemsToAdd.length > 0) {
      const data = itemsToAdd.length > 1 ? {items: itemsToAdd} : itemsToAdd[0];
      try {
        if (data.items) {
          data.items.forEach(item => {
            events.add.push(new dataLayerCartEvent(item, eventInfo));
          });
        } else {
          events.add.push(new dataLayerCartEvent(data, eventInfo));
        }
      } catch (e) {
        console.error(e);
        dispatch("toast/send", { text: e.message, type: "error" }, { root: true });
      }

      addPromise = axios.post("/cart/add.js", data);
    }
    
    return Promise.all([updatePromise, addPromise]).then((resolutions) => {
      const [updateResolve, addResolve] = resolutions;

      try {
        events.update.forEach(event => {
          event.send(updateResolve);
        });
  
        events.add.forEach(event => {
          event.send(addResolve);
        });
      } catch (e) {
        console.error(e);
        dispatch('toast/send', { text: e.message, type: 'error' }, { root: true });
      }

      return axios.get(`/cart.js?q=${new Date().getTime()}`);
    }).then(response => {
        commit('SET_CHECKOUT', response.data);
        commit('SET_KITS', findKits() || []);
        return response.data;
      })
      .catch(err => {
        console.error(err);
        if (Sentry) { Sentry.captureException(err); }
        dispatch('toast/send', { text: err.message, type: 'error' }, {root: true});
        return err;
      });
  },
  /**
   * @desc NOTE: Only updates quantity.
   * 
   * @typedef Item
   * @type {Object}
   * @prop {String} key
   * @prop {Number} quantity 
   * 
   * @param {Array<Item>} items
   */
  updateItems ({ state, commit, dispatch }, items) {
    if(!(items instanceof Array))
      items = [items];

    const updatedItemKeys = items.map(prop('key'));
    const lineItemQuantities = state.checkout.items
      .map((item) => items[updatedItemKeys.indexOf(item.key)] || item)
      .map(prop('quantity'));
    const payload = {
      updates: lineItemQuantities,
    };

    return axios.post('/cart/update.js', payload)
      .then(response => {
        commit('SET_CHECKOUT', response.data)
        commit('SET_KITS', findKits() || []);
        return response;
      })
      .catch(err => {
        console.error(err);
        if (Sentry) { Sentry.captureException(err); }
        dispatch('toast/send', { text: err.message, type: 'error' }, {root: true});
        return err;
      })
  },
  removeItems ({ dispatch }, items) {
    if(!(items instanceof Array))
      items = [items];
      items.forEach(item => item.quantity = 0);
    
      const updatedItemKeys = items.map(prop('key'));
      const selectedItem=  state.checkout.items.filter(item => updatedItemKeys.includes(item.key));

      const eventInfo = {
        name: 'remove-from-cart',
        ecomEvent: 'Remove'
      };

      try {
        let event= new dataLayerCartEvent(selectedItem[0], eventInfo);
         event.send({config:{url:"cart/remove.js"}});  
        } catch (e) {
          console.error(e);
          dispatch('toast/send', { text: e.message, type: 'error' }, { root: true });
        }

    return dispatch('updateItems', items);
  },
  openSidecart: ({ commit }) => {
    commit('CLOSE_CARTMODAL');
    commit('OPEN_SIDECART');
  },
  closeSidecart: ({ commit }) => commit('CLOSE_SIDECART'),
  openCartModal: ({ state, commit }) => {
    window.clearTimeout(state.cartModalTimerId);
    commit('OPEN_CARTMODAL');
  },
  closeCartModal: ({ commit }) => commit('CLOSE_CARTMODAL'),
  setKits: ({commit}, kits) => commit('SET_KITS', kits),
  afterAddToCart: ({ state: { cartModalEnabled }, dispatch, commit }) => {
    const action = cartModalEnabled ? 'openCartModal' : 'openSidecart';
    dispatch(action);

    if(action === 'openCartModal') {
      const timerId = setTimeout(() => dispatch('closeCartModal'), CART_MODAL_CLOSE_DELAY_MS);
      commit('SET_CART_MODAL_TIMER_ID', timerId);
    }
  }
}

// Searches current cart line items for those with a _kitId property, adds unique kit ids to
// an array, then separates all kit items into objects matching their unique id and returns
// them for state
const findKits = () => {
  if(state.checkout.items && state.checkout.items.length) {
    let kitItems = state.checkout.items
      .filter(prop('properties'))
      .filter(prop('properties._kitId'))
    let _kitIds = []

    kitItems.forEach(item => {
      if (!_kitIds.includes(item.properties._kitId)) {
        _kitIds.push(item.properties._kitId)
      }
    })

    return _kitIds.map(id => {
      return {
        id: id,
        items: kitItems.filter(item => item.properties._kitId === id)
          .sort((a, b) => b.price - a.price) // sort highest to lowest so lunchbox magnet isn't first
      }
    })
  }
}

const getters = {
  cartModalOpen ({ cartModalEnabled, cartModalOpen, checkout }) {
    return cartModalEnabled && cartModalOpen && checkout.items.length > 0; 
  }
}

export default { namespaced: true, state, mutations, actions, getters }
